// css
import styles from './styles.module.scss';

// components
import { Breadcrumbs, Text, Tabs, Pill, Image } from '@mantine/core';
import { Smartphone, Expand, Heart } from 'lucide-react';
import GameSimilar from '../GameSimilar';
import Head from 'next/head';

// hooks
import { useRouter } from 'next/router';
import { useEffect, useRef, useState, useMemo } from 'react';
import fetchGameCover from '@/utils/fetch/fetch.js';
import { useSelector, useDispatch } from 'react-redux';
import { recordCategoryClick } from '@/store/features/gameSlice';

// api
import { getSimilarGames } from '@/api/header';

export default function GameDetail({ game, gameCoverLoading, similarGamesProps }) {
    const { isMobile, categoryClickData } = useSelector((state) => state.game);
    const dispatch = useDispatch();
    const [similarGames, setSimilarGames] = useState(similarGamesProps || []);
    const [loading, setLoading] = useState(!similarGamesProps?.length);
    const iframeRef = useRef(null);
    const [currentTab, setCurrentTab] = useState('description');

    // 初始化 - 只有当没有服务端数据时才从客户端获取
    useEffect(() => {
        if (similarGamesProps && similarGamesProps.length > 0) {
            // 使用服务端数据，但需要获取封面图
            const gamesWithLoading = similarGamesProps.map((item) => ({...item, loading: true}));
            setSimilarGames(gamesWithLoading);
            fetchGameCover(gamesWithLoading, setSimilarGames);
            setLoading(false);
        } else {
            // 客户端获取相似游戏
            setLoading(true);
            const fetchSimilarGames = async () => {
                try {
                    const res = await getSimilarGames({
                        game_path: game.path,
                        limit: 10,
                        offset: 0,
                    });
                    let { results } = res.data;
                    results = results ? results.map((item) => ({...item, loading: true})) : [];
                    setSimilarGames(results);
                    fetchGameCover(results, setSimilarGames);
                } catch (e) {
                    console.error(e);
                } finally {
                    setLoading(false);
                }
            };
            fetchSimilarGames();
        }
    }, [similarGamesProps]);

    // 路由跳转
    const router = useRouter();
    const routerPush = (path) => {
        // 确保路径是绝对路径，避免相对路径跳转问题
        const absolutePath = path.startsWith('/') ? path : `/${path}`;
        
        // 如果不是首页路径，记录点击数据到 Redux
        if (absolutePath !== '/') {
            const categoryPath = absolutePath.replace('/', '');
            dispatch(recordCategoryClick({path}));
        }
        
        router.push({ pathname: absolutePath });
    }

    // 生成面包屑路径数据
    const getBreadcrumbData = (game) => {
        const { currentPath } = categoryClickData;
        const categorie = game.categories?.some((category) => category.path === currentPath) ? { path: currentPath } : game.categories?.[0] || null;
        
        if (!categorie) {
            return [{ name: 'Gameyeap', href: '/' }];
        } else {
            const [path, parentPath] = [categorie.path, categorie.parent?.path];
            const pathArray = [
                { name: 'Gameyeap', href: '/' },
                { name: path, href: path },
                { name: parentPath, href: `${path}/${parentPath}` }
            ];
            return pathArray.filter(item => item.name); // 过滤掉空的项目
        }
    };

    // 生成面包屑结构化数据
    const generateBreadcrumbSchema = (game) => {
        const { currentPath } = categoryClickData;
        const categorie = game.categories?.some((category) => category.path === currentPath) ? { path: currentPath } : game.categories?.[0] || null;
        
        if (!categorie) {
            return null; // 如果没有分类，不生成结构化数据
        }

        const itemListElement = [
            {
                "@type": "ListItem",
                "position": 1,
                "name": categorie.path,
                "item": `https://www.gameyeap.com/${categorie.path}`
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": game.path,
                "item": `https://www.gameyeap.com/game/${game.path}`
            }
        ];

        return {
            "@context": "https://www.gameyeap.com",
            "@type": "BreadcrumbList",
            "itemListElement": itemListElement
        };
    };

    // 格式化游戏路径（UI 渲染）
    const formatPath = (game) => {
        const breadcrumbData = getBreadcrumbData(game);
        
        if (breadcrumbData.length === 1) {
            return (<a className={styles.path_item} onClick={() => routerPush('/')}>Gameyeap</a>);
        } else {
            const pathItems = breadcrumbData.map((item, index) => {
                return (
                    <Text key={index} component="a" className={styles.path_item} onClick={() => routerPush(item.href)}>
                        {item.name}
                    </Text>
                )
            });
            return (
                <Breadcrumbs>
                    { pathItems }
                </Breadcrumbs>
            );
        }
    };

    // 全屏
    const handleMaximize = () => {
        const iframe = iframeRef.current;
        if (iframe.requestFullscreen) {
            iframe.requestFullscreen();
        } else if (iframe.webkitRequestFullscreen) {
            iframe.webkitRequestFullscreen();
        } else if (iframe.mozRequestFullScreen) {
            iframe.mozRequestFullScreen();
        } else if (iframe.msRequestFullscreen) {
            iframe.msRequestFullscreen();
        }
    }

    // TODO 检测当前封面是否正在加载(只针对手机端)
    const checkGameCoverLoading = useMemo(() => {
        return isMobile && !gameCoverLoading && (game?.cover_image != '/images/icons/common/placeholder.jpg');
    }, [gameCoverLoading, game]);

    return (
        <>
            <Head>
                {generateBreadcrumbSchema(game) && (
                    <script 
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(generateBreadcrumbSchema(game))
                        }}
                    />
                )}
            </Head>
            <>
                <div className={`${styles.main_content} ${styles.container}`}>
                {/* 游戏部分 */}
                <div className={styles.game_iframe}>
                    {/* TODO 缩略图 */}
                    {/*{ gameCoverLoading && <LoadingOverlay*/}
                    {/*    visible={true}*/}
                    {/*    zIndex={2000}*/}
                    {/*    overlayProps={{ radius: 'md', opacity: 0.1 }}*/}
                    {/*    loaderProps={{ color: 'white', type: 'bars' }}*/}
                    {/*/> }*/}

                    {/*{ checkGameCoverLoading &&*/}
                    {/*    <Image*/}
                    {/*    src={game.cover_image}*/}
                    {/*    alt={game.name}*/}
                    {/*    className={styles.image}*/}
                    {/*/>}*/}

                    <iframe
                        ref={iframeRef}
                        src={game.src}
                        title={game.name}
                        allow="fullscreen; autoplay; camera; microphone; gamepad"
                        allowFullScreen
                    />
                </div>

                {/* 按钮栏 */}
                <div className={styles.iframe_button_group}>
                    <Expand strokeWidth={1.5} size={24} onClick={handleMaximize} className={styles.maxi_mize}/>
                </div>

                {/* 底部内容 */}
                <div className={styles.content}>
                    {/* 游戏内容 */}
                    <div className={styles.game_content}>
                        <div className={styles.path}>
                            {formatPath(game)}
                        </div>
                        <div className={styles.title}>
                            <div className={styles.cover_image_block}>
                                <Image
                                    src={game.cover_image}
                                    alt={`Game ${game.name}`}
                                    className={styles.image}
                                />
                            </div>
                            <div className={styles.game_name}>
                                <h1 style={{fontSize: "24px"}}>{game.name}</h1>
                                <div className={styles.flex_row_start}>
                                    <div className={styles.tag}>
                                        { game.is_mobile_ready && <Smartphone color="#645656" strokeWidth={3} size={20}/> }
                                    </div>
                                    <Pill size="xl" color="red" variant="outline" classNames={{root: styles.pill, label: styles.pill_label}}>
                                        <Heart size={16} color="#f1131e" strokeWidth={3}/>
                                        <div className={styles.clicked_count}>{ game.clicked_count }</div>
                                    </Pill>
                                </div>
                            </div>
                        </div>

                        <div className={styles.game_tab}>
                            <div className={styles.game_tab_select}>
                                <div 
                                    className={`${styles.tab_item} ${currentTab === 'description' ? styles.active : ''}`}
                                    onClick={() => setCurrentTab('description')}
                                >
                                    <img 
                                        src={`/images/icons/common/game-line-${currentTab === 'description' ? 'light' : 'dark'}.svg`}
                                        alt="Description"
                                    />
                                    <span className={styles.font_normal}>Description</span>
                                </div>
                                <div 
                                    className={`${styles.tab_item} ${currentTab === 'introduction' ? styles.active : ''}`}
                                    onClick={() => setCurrentTab('introduction')}
                                >
                                    <img 
                                        src={`/images/icons/common/gamepad-line-${currentTab === 'description' ? 'light' : 'dark'}.svg`}
                                        alt="Introduction"
                                    />
                                    <span className={styles.font_normal}>Introduction</span>
                                </div>
                            </div>

                            <div className={styles.game_tab_content}>
                                {currentTab === 'description' ? (
                                    <p className={styles.description_content}>
                                        {game.description}
                                    </p>
                                ) : (
                                    <p className={styles.introduction_content}>
                                        {game.instructions}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 推荐游戏 */}
                    <div className={styles.recommend_games}>
                        <h2 className={styles.recommend_games_title}>Recommended Similar Games</h2>
                        <GameSimilar gameList={similarGames} loading={loading} />
                    </div>
                </div>
                </div>
            </>
        </>
    );
} 